export function formatDateUTC(
  dateInput: string | number | Date,
  format: 'MMM DD, YYYY' | 'MM/DD/YYYY' | 'YYYY.MM.DD' | string = 'MMM DD, YYYY'
): string {
  const date = new Date(dateInput);

  if (isNaN(date.getTime())) {
    throw new Error(`Invalid date input: ${dateInput}`);
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  }).formatToParts(date);

  const map: Record<string, string> = Object.fromEntries(parts.map((p) => [p.type, p.value]));

  const replacements: Record<string, string> = {
    MMM: map.month, // e.g., "Apr"
    MM: String(date.getUTCMonth() + 1).padStart(2, '0'),
    DD: map.day,
    YYYY: map.year,
  };

  // Replace longer tokens first to avoid MM/M conflict
  return format.replace(/YYYY|MMM|MM|DD/g, (match: string) => replacements[match] || match);
}
export function formatLocalDateWithZone(
  dateInput: string | number | Date,
  format: 'MMM DD, YYYY' | 'MM/DD/YYYY' | 'YYYY.MM.DD' | string = 'DD MMM YYYY, HH:mm z (X, City)'
): string {
  const date = new Date(dateInput);

  if (isNaN(date.getTime())) {
    throw new Error(`Invalid date input: ${dateInput}`);
  }

  // Get user time zone info
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const offsetMinutes = -date.getTimezoneOffset(); // Local offset from UTC
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const absOffset = Math.abs(offsetMinutes);
  const offsetHours = String(Math.floor(absOffset / 60)).padStart(2, '0');
  const offsetMins = String(absOffset % 60).padStart(2, '0');
  const utcOffset = `UTC ${sign}${offsetHours}:${offsetMins}`;

  // Format date using locale-aware formatter
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  }).formatToParts(date);

  const map: Record<string, string> = Object.fromEntries(parts.map((p) => [p.type, p.value]));

  const city = timeZone.split('/')[1]?.replace('_', ' ') || timeZone;
  const replacements: Record<string, string> = {
    MMM: map.month, // e.g. Apr
    MM: String(date.getMonth() + 1).padStart(2, '0'),
    DD: map.day,
    YYYY: map.year,
    HH: map.hour,
    mm: map.minute,
    z: map.timeZoneName,
    X: utcOffset,
    City: city,
  };

  const formattedDate = format?.replace(
    /YYYY|MMM|MM|DD|HH|mm|z|X|City/g,
    (match) => replacements[match] || match
  );

  return formattedDate;
}
